import { defineStore } from 'pinia'
import { useConfigStore } from './configStore'
import { storeToRefs } from 'pinia'

export type PlanTier = 'grow' | 'scale' | 'pro' | 'legacy'

export const useSubscriptionStore = defineStore('subscription', () => {
  const configStore = useConfigStore()
  const { owner } = storeToRefs(configStore)

  // Computed properties to access owner attributes
  const stripeStatus = computed(() => owner.value?.stripe_status)
  const trialEndsAt = computed(() => owner.value?.trial_ends_at)
  const isFreeAccount = computed(() => owner.value?.free_account)
  const isAdmin = computed(() => owner.value?.is_admin)
  const activeSubscription = computed(() => owner.value?.active_subscription ?? null)
  const resolvedSubscriptionStatus = computed(() => {
    return activeSubscription.value?.stripe_status ?? stripeStatus.value ?? null
  })

  const hasValidSubscription = computed(() => {
    const subscription = activeSubscription.value
    if (!subscription) {
      return false
    }

    if (resolvedSubscriptionStatus.value !== 'active') return false
    return !subscription.ends_at
  })

  // =========================================================================
  // Tier-aware plan properties (ENG-855)
  // =========================================================================

  /** Current plan tier: 'grow', 'scale', 'pro', or 'legacy' */
  const planTier = computed<PlanTier>(() => owner.value?.plan?.tier ?? 'grow')

  /** Full list of features available to the user (already merged by tier on backend) */
  const planFeatures = computed<string[]>(() => owner.value?.plan?.features ?? [])

  /** ESP providers available to the user */
  const planEspProviders = computed<string[]>(() => owner.value?.plan?.esp_providers ?? [])

  /** Project and collaborator limits with current usage */
  const planLimits = computed(() => owner.value?.plan?.limits ?? {
    projects: { current: 0, max: 1 },
    collaborators: { current: 0, max: 1 },
  })

  /** Billing model: 'legacy' (single subscription covers all teams) or 'per_team' (each project has its own subscription) */
  const billingModel = computed<'legacy' | 'per_team'>(() => owner.value?.billing_model ?? 'per_team')

  /** Whether this user is on the legacy billing model (single subscription covers all teams) */
  const isLegacyBilling = computed(() => billingModel.value === 'legacy')

  /** Whether this user is on per-team billing (each project needs its own subscription) */
  const isPerTeamBilling = computed(() => billingModel.value === 'per_team')

  /**
   * Which pricing the user signed up under: 'v1' (old visitor-based) or 'v2' (new feature-based).
   * NULL defaults to 'v1' (grandfathered, fail-safe) to stay consistent with the backend —
   * PlanService::getUserTier/getTeamTier and BillingController both use `?? 'v1'`. A 'v2'
   * default here would diverge from backend access and show the wrong billing banner/UI.
   */
  const pricingVersion = computed(() => owner.value?.pricing_version ?? 'v1')

  /** Whether this user signed up under the old pricing model */
  const isLegacyPricing = computed(() => pricingVersion.value === 'v1')

  /** Legacy accounts (old plans) get unrestricted access */
  const isLegacy = computed(() => planTier.value === 'legacy')

  /** Free builder (reverse trial: signed up but hasn't published yet) */
  const isFreeBuilder = computed(() => owner.value?.is_free_builder === true)

  /** Users with unrestricted access: legacy, free builders, free/admin accounts.
   *  Trial users with a subscription have chosen a tier and should be restricted to it. */
  const hasUnrestrictedAccess = computed(() =>
    isLegacy.value || isFreeBuilder.value || isFreeAccount.value || isAdmin.value
  )

  /** Check if user has access to a specific plan feature */
  const hasFeature = (feature: string): boolean => {
    if (hasUnrestrictedAccess.value) return true
    return planFeatures.value.includes(feature)
  }

  /** Check if user has at least the specified tier level */
  const hasTierOrHigher = (tier: PlanTier): boolean => {
    if (hasUnrestrictedAccess.value) return true
    const tierOrder: PlanTier[] = ['grow', 'scale', 'pro']
    const userIndex = tierOrder.indexOf(planTier.value)
    const requiredIndex = tierOrder.indexOf(tier)
    return userIndex >= requiredIndex
  }

  /**
   * Pre-subscription setup state for reverse-trial users.
   * The current-team payload should set `is_free_builder` for unpaid teams even
   * when another team already has a trial, but we keep the legacy-shaped
   * fallback for older payloads that have not been updated yet.
   */
  const isPreSubscriptionSetup = computed(() => {
    if (!owner.value) return false
    if (isFreeAccount.value || isAdmin.value) return false
    if (isFreeBuilder.value) return true
    return !activeSubscription.value && !resolvedSubscriptionStatus.value && !owner.value.trial_ends_at
  })

  /**
   * Determines whether the user should be treated as active inside the app.
   * Active users are either:
   * 1. Free or admin accounts
   * 2. Pre-subscription reverse-trial users
   * 3. In a trial period
   * 4. Have an active Stripe subscription
   *
   * Having a card on file does not imply an active subscription.
   *
   * Returns true while owner data is loading to prevent
   * flashing "inactive" state before data is available.
   */
  const isActive = computed(() => {
    // If owner hasn't loaded yet, assume active to prevent flash of inactive state
    if (!owner.value) return true
    if (isPreSubscriptionSetup.value) return true
    if (isTrialing.value) return true
    const activeStripeStatuses = ['trialing', 'active']
    return (
      isFreeAccount.value ||
      isAdmin.value ||
      activeStripeStatuses.includes(resolvedSubscriptionStatus.value)
    )
  })

  /**
   * Determines if the user is currently in trial period
   */
  const isTrialing = computed(() => {
    if (owner.value?.on_trial) return true
    return resolvedSubscriptionStatus.value === 'trialing'
  })

  const trialExpiresAt = computed(() => {
    if (owner.value?.on_trial) return owner.value?.trial_ends_at
    return null
  })

  /**
   * Whether the user has a payment method on file.
   * Used to distinguish between trial-expired (never paid) and payment-failed (did pay once).
   */
  const hasPaymentMethod = computed(() => owner.value?.has_payment_method ?? false)

  /**
   * Trial has expired and user never subscribed.
   * This is true when the account is inactive AND they have no payment method on file.
   * Note: Some accounts may have an active_subscription record from trial setup,
   * so we use has_payment_method as the definitive signal.
   */
  const isTrialExpired = computed(() => {
    if (!owner.value) return false
    if (isActive.value) return false
    // If they have no payment method, they never actually subscribed
    return !owner.value.has_payment_method
  })

  /**
   * Actionable billing failure state for app UI.
   * Free accounts remain billing-exempt even when stale Stripe subscription data
   * still reports past_due or unpaid. (ENG-1167)
   */
  const isPaymentFailed = computed(() => {
    if (!owner.value) return false
    if (isFreeAccount.value) return false
    if (isTrialing.value) return false
    return ['past_due', 'unpaid'].includes(resolvedSubscriptionStatus.value)
  })

  /**
   * User had an active subscription that has been canceled (not payment failure).
   * Distinguished from isTrialExpired (never paid) and isPaymentFailed (past_due/unpaid).
   */
  const isCanceled = computed(() => {
    if (!owner.value) return false
    if (isActive.value) return false
    if (isPaymentFailed.value) return false
    if (isTrialExpired.value) return false
    return hasPaymentMethod.value && resolvedSubscriptionStatus.value === 'canceled'
  })

  /**
   * User has not started a paid or trialing subscription yet.
   * Free builders remain in this state even after adding a payment method,
   * because card-on-file and subscribed are separate concepts.
   */
  const hasNoSubscription = computed(() => {
    if (!owner.value) return false
    if (isTrialing.value) return false
    if (hasValidSubscription.value) return false
    return isPreSubscriptionSetup.value
  })

  const needsCurrentTeamBillingSetup = computed(() => {
    if (!owner.value) return false
    if (!isPerTeamBilling.value) return false
    if (isFreeAccount.value || isAdmin.value) return false
    return hasNoSubscription.value
  })

  return {
    stripeStatus,
    trialEndsAt,
    trialExpiresAt,
    isFreeAccount,
    isAdmin,
    isActive,
    isTrialing,
    isTrialExpired,
    isPaymentFailed,
    isCanceled,
    hasPaymentMethod,
    hasNoSubscription,
    needsCurrentTeamBillingSetup,
    activeSubscription,
    hasValidSubscription,
    // Tier-aware properties
    planTier,
    planFeatures,
    planEspProviders,
    planLimits,
    isLegacy,
    isFreeBuilder,
    isPreSubscriptionSetup,
    hasUnrestrictedAccess,
    hasFeature,
    hasTierOrHigher,
    // Billing model
    billingModel,
    isLegacyBilling,
    isPerTeamBilling,
    // Pricing version
    pricingVersion,
    isLegacyPricing
  }
})
